import { lazy, Suspense, type ReactNode } from 'react';
import Skeleton from '@mui/material/Skeleton';
import { createRoute, redirect, useNavigate, type AnyRoute } from '@tanstack/react-router';
import { z } from 'zod';
import { listSearchSchema } from '@eis/core';
import { insurancePermissions as p } from '../permissions';
import {
  GroupPolicyList,
  groupPolicyStatusTabs,
} from '../features/group-policies/ui/GroupPolicyList';
import {
  GroupPolicyDetail,
  type GroupPolicyTab,
} from '../features/group-policies/ui/GroupPolicyDetail';
import { GroupPolicyWizard } from '../features/group-policies/ui/GroupPolicyWizard';
import { TarifListPage } from '../features/tarifs/ui/TarifListPage';
import { TarifDetailPage } from '../features/tarifs/ui/TarifDetailPage';
import { GenerationDetailPage } from '../features/tarifs/ui/GenerationDetailPage';
import { GroupTarifListPage } from '../features/group-tarifs/ui/GroupTarifListPage';
import { GroupTarifDetailPage } from '../features/group-tarifs/ui/GroupTarifDetailPage';
import { GroupGenerationDetailPage } from '../features/group-tarifs/ui/GroupGenerationDetailPage';
/**
 * The authoring surface is loaded on demand (prompt 15.2, and the bundle budget it answers to).
 *
 * These three screens are the schema-driven editors, and they are reachable only from Produkte.
 * A `lazy()` boundary here is what makes `module-insurance-authoring` a chunk somebody fetches
 * when they open a tarif rather than one every persona downloads on entering the module — the
 * split has to be real, or the budget is measuring an accounting entry instead of a download.
 */
const TarifCreatePage = lazy(async () => ({
  default: (await import('../features/tarif-authoring/ui/TarifCreatePage')).TarifCreatePage,
}));
const TarifAssetEditorPage = lazy(async () => ({
  default: (await import('../features/tarif-authoring/ui/TarifAssets')).TarifAssetEditorPage,
}));
const GroupTarifAssetEditorPage = lazy(async () => ({
  default: (await import('../features/tarif-authoring/ui/GroupTarifAssets'))
    .GroupTarifAssetEditorPage,
}));

/** One placeholder for all three, so a chunk fetch never renders as a blank page. */
const authoringFallback = <Skeleton variant="rounded" height={320} />;
import { PolicyListPage } from '../features/policies/ui/PolicyListPage';
import { PolicyDetailPage, type PolicyTab } from '../features/policies/ui/PolicyDetailPage';
import { LeadListPage } from '../features/leads/ui/LeadListPage';
import { LeadCreatePage } from '../features/leads/ui/LeadCreatePage';
import { LeadDetailPage, type LeadTab } from '../features/leads/ui/LeadDetailPage';

/**
 * The insurance route tree (backbone §3).
 *
 * Permissions are declared ONCE here in `staticData`; `beforeLoad` enforces them and the
 * manifest's nav is filtered from the same declarations, so guard and menu cannot drift.
 * Search params are Zod-validated — a mistyped `?tab=` fails validation instead of rendering
 * an undefined tab.
 *
 * Route components are defined inside the factory so they can close over the route objects and
 * use the typed `route.useParams()` / `route.useSearch()` hooks.
 */
/**
 * The shell composes module route trees at runtime rather than through TanStack's file-based
 * codegen, so the parent is `AnyRoute` and param/search inference cannot reach these components.
 * The Zod schemas below are the actual runtime guarantee — `validateSearch` rejects anything that
 * does not match before a component ever renders — so the shapes are stated once here instead of
 * leaking `any` into every consumer.
 */
/**
 * Every list's state is in the URL (SDD-017 §9): `filters`, `search`, `order` (and `validAt` on a
 * temporal list) from `listSearchSchema`, plus the screen's own quick choices.
 */
/** The list's status tab (prompt 24 Phase 24.4) — in the URL, so a filtered list is a link. */
const groupPolicyListSearchSchema = listSearchSchema.extend({
  status: z.enum(groupPolicyStatusTabs).default('all'),
});
/** The policy list's tarif (it decides which product fields the filter offers). */
const policyListSearchSchema = listSearchSchema.extend({ tarif: z.string().optional() });
/** The lead list's membership quick choice (SDD-013 R-1: standalone by default). */
const leadListSearchSchema = listSearchSchema.extend({
  membership: z.enum(['STANDALONE', 'MEMBER', 'ALL']).optional(),
});

/**
 * The detail's tab and as-of date — and, for the members tab, its list state (SDD-021 §9, rule
 * 8d): the day (`validAt`) or the period (`coveredFrom`/`coveredTo`), the filter, the search and
 * the order, from `listSearchSchema`. A tab change drops them (the route navigates with the tab
 * alone).
 */
const groupPolicyDetailSearchSchema = listSearchSchema.extend({
  tab: z
    .enum(['overview', 'policies', 'invoices', 'timeline', 'leads', 'claims'])
    .default('overview'),
  /** As-of date for time-travel reads; civil date only. */
  asOf: z.iso.date().optional(),
});
/**
 * Params and search come back as `any` because the parent is `AnyRoute` — the shell composes
 * module trees at runtime rather than through TanStack's file-based codegen. Narrowing through
 * `unknown` at each use site keeps `any` from leaking into the route components; the Zod
 * `validateSearch` above is the runtime guarantee that the shapes actually hold.
 */
interface GroupPolicyDetailParams {
  readonly groupPolicyId: string;
}
interface TarifParams {
  readonly slug: string;
}
interface GenerationParams {
  readonly slug: string;
  readonly generation: string;
}
interface AssetParams {
  readonly slug: string;
  readonly name: string;
}
interface PolicyParams {
  readonly code: string;
}
const policyDetailSearchSchema = z.object({
  tab: z.enum(['overview', 'timeline', 'billing', 'claims']).default('overview'),
  /** The lead this policy was just accepted from (SDD-013 §8.1.3): the toast that links back. */
  fromLead: z.string().optional(),
  /** The business date of the belief shown (SDD-017 §6); today when absent. Civil date. */
  validAt: z.iso.date().optional().catch(undefined),
  /**
   * The system time the belief is read as recorded at (`…Z`); now when absent. A CIVIL date here is
   * a link from before SDD-017, when `asOf` meant the business date — read as `validAt`.
   */
  asOf: z.union([z.iso.date(), z.iso.datetime()]).optional().catch(undefined),
  /** The catalog key of a field to highlight — the field a search hit matched. */
  hl: z.string().optional(),
});
type PolicyDetailSearch = z.infer<typeof policyDetailSearchSchema>;
/**
 * A "Tarif & Deckung" asset's tab, intent and highlighted variant (ui-spec §10: the generation
 * preview links "Tarifvariante anlegen" and a variant card here). Other asset types ignore them.
 */
const assetSearchSchema = z.object({
  tab: z.enum(['variants', 'claimConfig', 'overview']).optional().catch(undefined),
  intent: z.enum(['addVariant']).optional().catch(undefined),
  variant: z.string().optional().catch(undefined),
});
type AssetSearch = z.infer<typeof assetSearchSchema>;
const leadDetailSearchSchema = z.object({
  tab: z.enum(['overview', 'history']).default('overview'),
});
type LeadDetailSearch = z.infer<typeof leadDetailSearchSchema>;
type GroupPolicyDetailSearch = z.infer<typeof groupPolicyDetailSearchSchema>;

interface GuardContext {
  readonly permissions: { canGroups: (groups: readonly (readonly string[])[]) => boolean };
}

const guard =
  (groups: readonly (readonly string[])[]) =>
  ({ context }: { context: GuardContext }): void => {
    if (!context.permissions.canGroups(groups)) {
      // TanStack signals navigation by throwing this sentinel — control flow, not an error.
      // eslint-disable-next-line @typescript-eslint/only-throw-error
      throw redirect({ to: '/403' });
    }
  };

export function createRoutes(parentRoute: AnyRoute): AnyRoute[] {
  const groupPoliciesIndex = createRoute({
    getParentRoute: () => parentRoute,
    path: '/group-policies',
    validateSearch: groupPolicyListSearchSchema,
    staticData: { permissions: [[p.groupPolicies.view]] },
    beforeLoad: guard([[p.groupPolicies.view]]),
    // The tab, the filter, the search and the order are all the URL's (`useListSearch`).
    component: GroupPolicyList,
  });

  const groupPolicyCreate = createRoute({
    getParentRoute: () => parentRoute,
    path: '/group-policies/new',
    staticData: { permissions: [[p.groupPolicies.create]] },
    beforeLoad: guard([[p.groupPolicies.create]]),
    component: function GroupPolicyWizardRoute(): ReactNode {
      const navigate = useNavigate();
      return (
        <GroupPolicyWizard
          onCreated={(groupPolicyId) => {
            void navigate({
              to: '/insurance/group-policies/$groupPolicyId',
              params: { groupPolicyId },
            });
          }}
          onCancel={() => {
            void navigate({ to: '/insurance/group-policies' });
          }}
        />
      );
    },
  });

  const groupPolicyDetail = createRoute({
    getParentRoute: () => parentRoute,
    path: '/group-policies/$groupPolicyId',
    validateSearch: groupPolicyDetailSearchSchema,
    staticData: { permissions: [[p.groupPolicies.view]] },
    beforeLoad: guard([[p.groupPolicies.view]]),
    component: function GroupPolicyDetailRoute(): ReactNode {
      const navigate = useNavigate();
      const { groupPolicyId } = groupPolicyDetail.useParams() as unknown as GroupPolicyDetailParams;
      const { tab } = groupPolicyDetail.useSearch() as unknown as GroupPolicyDetailSearch;
      return (
        <GroupPolicyDetail
          groupPolicyId={groupPolicyId}
          tab={tab}
          onTabChange={(next: GroupPolicyTab) => {
            void navigate({
              to: '/insurance/group-policies/$groupPolicyId',
              params: { groupPolicyId },
              search: { tab: next },
            });
          }}
        />
      );
    },
  });

  // ---------------------------------------------------------------------------------------
  // Produkte — Tarife (prompt 11.1). Version vocabulary is allowed on this surface and on no
  // other (SSOT I-6): these are the product-engineering persona's screens.
  // ---------------------------------------------------------------------------------------

  const tarifsIndex = createRoute({
    getParentRoute: () => parentRoute,
    path: '/tarifs',
    validateSearch: listSearchSchema,
    staticData: { permissions: [[p.products.view]] },
    beforeLoad: guard([[p.products.view]]),
    component: TarifListPage,
  });

  /**
   * The one entry point that creates configuration (prompt 15.1).
   *
   * Declared with BOTH create actions as alternative groups, because the screen mints two
   * different aggregates: `products.create` for a Tarif, `groupTariffs.create` for a Group Tarif.
   * A persona holding one and not the other reaches the chooser and sees only the branch they may
   * walk — the alternative would be a 403 for somebody who is entitled to half the screen
   * (modules/insurance/PERMISSIONS.md).
   *
   * It sits BEFORE `/tarifs/$slug` in the returned array so `new` cannot be read as a slug.
   */
  const tarifCreate = createRoute({
    getParentRoute: () => parentRoute,
    path: '/tarifs/new',
    staticData: { permissions: [[p.products.create], [p.groupTariffs.create]] },
    beforeLoad: guard([[p.products.create], [p.groupTariffs.create]]),
    component: function TarifCreateRoute(): ReactNode {
      const navigate = useNavigate();
      return (
        <Suspense fallback={authoringFallback}>
          <TarifCreatePage
            onCreated={(kind, slug) => {
              // The detail page, never the list: the operator's next action is "add an asset".
              void navigate(
                kind === 'tarif'
                  ? { to: '/insurance/tarifs/$slug', params: { slug } }
                  : { to: '/insurance/group-tarifs/$slug', params: { slug } },
              );
            }}
            onCancel={() => {
              void navigate({ to: '/insurance/tarifs' });
            }}
          />
        </Suspense>
      );
    },
  });

  const tarifDetail = createRoute({
    getParentRoute: () => parentRoute,
    path: '/tarifs/$slug',
    staticData: { permissions: [[p.products.view]] },
    beforeLoad: guard([[p.products.view]]),
    component: function TarifDetailRoute(): ReactNode {
      const { slug } = tarifDetail.useParams() as unknown as TarifParams;
      return <TarifDetailPage slug={slug} />;
    },
  });

  const generationDetail = createRoute({
    getParentRoute: () => parentRoute,
    path: '/tarifs/$slug/generations/$generation',
    staticData: { permissions: [[p.products.view]] },
    beforeLoad: guard([[p.products.view]]),
    component: function GenerationDetailRoute(): ReactNode {
      const { slug, generation } = generationDetail.useParams() as unknown as GenerationParams;
      return <GenerationDetailPage slug={slug} generation={generation} />;
    },
  });

  /**
   * One asset, opened from its owner's list (prompt 15.2).
   *
   * Guarded with `view`, not `update`: reading a published asset is a normal thing for anybody who
   * may see the product, and it is the largest read-only surface the Produkte persona has. The
   * mutating actions inside carry their own gates — `products.update` to save a draft,
   * `products.publish` to freeze one (modules/insurance/PERMISSIONS.md).
   */
  const tarifAssetDetail = createRoute({
    getParentRoute: () => parentRoute,
    path: '/tarifs/$slug/assets/$name',
    validateSearch: assetSearchSchema,
    staticData: { permissions: [[p.products.view]] },
    beforeLoad: guard([[p.products.view]]),
    component: function TarifAssetRoute(): ReactNode {
      const { slug, name } = tarifAssetDetail.useParams() as unknown as AssetParams;
      const search = tarifAssetDetail.useSearch() as unknown as AssetSearch;
      // Keyed on the asset it is editing: the editor holds the working document in state, and
      // navigating between two assets must start from the second one rather than merge them.
      return (
        <Suspense fallback={authoringFallback}>
          <TarifAssetEditorPage key={`${slug}/${name}`} slug={slug} name={name} search={search} />
        </Suspense>
      );
    },
  });

  // ---------------------------------------------------------------------------------------
  // Produkte — Gruppen-Tarife (prompt 11.2). Same shape one layer up: a group version pins an
  // EXACT physical child version, which is what makes the pin chain reproducible (S-3).
  // ---------------------------------------------------------------------------------------

  const groupTarifsIndex = createRoute({
    getParentRoute: () => parentRoute,
    path: '/group-tarifs',
    validateSearch: listSearchSchema,
    staticData: { permissions: [[p.groupTariffs.view]] },
    beforeLoad: guard([[p.groupTariffs.view]]),
    component: GroupTarifListPage,
  });

  const groupTarifDetail = createRoute({
    getParentRoute: () => parentRoute,
    path: '/group-tarifs/$slug',
    staticData: { permissions: [[p.groupTariffs.view]] },
    beforeLoad: guard([[p.groupTariffs.view]]),
    component: function GroupTarifDetailRoute(): ReactNode {
      const { slug } = groupTarifDetail.useParams() as unknown as TarifParams;
      return <GroupTarifDetailPage slug={slug} />;
    },
  });

  const groupGenerationDetail = createRoute({
    getParentRoute: () => parentRoute,
    path: '/group-tarifs/$slug/generations/$generation',
    staticData: { permissions: [[p.groupTariffs.view]] },
    beforeLoad: guard([[p.groupTariffs.view]]),
    component: function GroupGenerationDetailRoute(): ReactNode {
      const { slug, generation } = groupGenerationDetail.useParams() as unknown as GenerationParams;
      return <GroupGenerationDetailPage slug={slug} generation={generation} />;
    },
  });

  const groupTarifAssetDetail = createRoute({
    getParentRoute: () => parentRoute,
    path: '/group-tarifs/$slug/assets/$name',
    validateSearch: assetSearchSchema,
    staticData: { permissions: [[p.groupTariffs.view]] },
    beforeLoad: guard([[p.groupTariffs.view]]),
    component: function GroupTarifAssetRoute(): ReactNode {
      const { slug, name } = groupTarifAssetDetail.useParams() as unknown as AssetParams;
      const search = groupTarifAssetDetail.useSearch() as unknown as AssetSearch;
      return (
        <Suspense fallback={authoringFallback}>
          <GroupTarifAssetEditorPage
            key={`${slug}/${name}`}
            slug={slug}
            name={name}
            search={search}
          />
        </Suspense>
      );
    },
  });

  // ---------------------------------------------------------------------------------------
  // Policen (prompt 11.3) — the operations persona's surface. NO version vocabulary here
  // (SSOT I-6); a policy's coordinates are stated as "Stand" and "Gültig".
  // ---------------------------------------------------------------------------------------

  const policiesIndex = createRoute({
    getParentRoute: () => parentRoute,
    path: '/policies',
    validateSearch: policyListSearchSchema,
    staticData: { permissions: [[p.policies.view]] },
    beforeLoad: guard([[p.policies.view]]),
    component: PolicyListPage,
  });

  const policyDetail = createRoute({
    getParentRoute: () => parentRoute,
    path: '/policies/$code',
    validateSearch: policyDetailSearchSchema,
    staticData: { permissions: [[p.policies.view]] },
    beforeLoad: guard([[p.policies.view]]),
    component: function PolicyDetailRoute(): ReactNode {
      const navigate = useNavigate();
      const { code } = policyDetail.useParams() as unknown as PolicyParams;
      const search = policyDetail.useSearch() as unknown as PolicyDetailSearch;
      const legacyDate = search.asOf !== undefined && !search.asOf.includes('T');
      const validAt = search.validAt ?? (legacyDate ? search.asOf : undefined);
      const asOf = legacyDate ? undefined : search.asOf;
      const cut = {
        ...(validAt === undefined ? {} : { validAt }),
        ...(asOf === undefined ? {} : { asOf }),
        ...(search.hl === undefined ? {} : { hl: search.hl }),
      };
      return (
        <PolicyDetailPage
          code={code}
          tab={search.tab}
          fromLead={search.fromLead}
          validAt={validAt}
          asOf={asOf}
          hl={search.hl}
          onTabChange={(next: PolicyTab) => {
            void navigate({
              to: '/insurance/policies/$code',
              params: { code },
              // The cut travels with the tab; the way back does not need to.
              search: { tab: next, ...cut },
            });
          }}
          onCutChange={(change) => {
            void navigate({
              to: '/insurance/policies/$code',
              params: { code },
              search: {
                tab: search.tab,
                ...(change.validAt === undefined ? {} : { validAt: change.validAt }),
                ...(change.asOf === undefined || change.asOf === '' ? {} : { asOf: change.asOf }),
                ...(search.hl === undefined ? {} : { hl: search.hl }),
              },
            });
          }}
        />
      );
    },
  });

  // ---------------------------------------------------------------------------------------
  // Leads (SDD-013): the offers that may become policies. `[[leads.view]]` for the list and the
  // detail, `[[leads.create]]` for the create route (§8.2 step 4). No version vocabulary (SSOT I-6).
  // ---------------------------------------------------------------------------------------

  const leadsIndex = createRoute({
    getParentRoute: () => parentRoute,
    path: '/leads',
    validateSearch: leadListSearchSchema,
    staticData: { permissions: [[p.leads.view]] },
    beforeLoad: guard([[p.leads.view]]),
    component: LeadListPage,
  });

  const leadCreate = createRoute({
    getParentRoute: () => parentRoute,
    path: '/leads/new',
    staticData: { permissions: [[p.leads.create]] },
    beforeLoad: guard([[p.leads.create]]),
    component: LeadCreatePage,
  });

  const leadDetail = createRoute({
    getParentRoute: () => parentRoute,
    path: '/leads/$code',
    validateSearch: leadDetailSearchSchema,
    staticData: { permissions: [[p.leads.view]] },
    beforeLoad: guard([[p.leads.view]]),
    component: function LeadDetailRoute(): ReactNode {
      const navigate = useNavigate();
      const { code } = leadDetail.useParams() as unknown as PolicyParams;
      const { tab } = leadDetail.useSearch() as unknown as LeadDetailSearch;
      return (
        <LeadDetailPage
          code={code}
          tab={tab}
          onTabChange={(next: LeadTab) => {
            void navigate({
              to: '/insurance/leads/$code',
              params: { code },
              search: { tab: next },
            });
          }}
        />
      );
    },
  });

  return [
    policiesIndex,
    policyDetail,
    leadsIndex,
    leadCreate,
    leadDetail,
    groupPoliciesIndex,
    groupPolicyCreate,
    groupPolicyDetail,
    tarifsIndex,
    tarifCreate,
    tarifDetail,
    tarifAssetDetail,
    generationDetail,
    groupTarifsIndex,
    groupTarifDetail,
    groupTarifAssetDetail,
    groupGenerationDetail,
  ];
}
